// Supabase auth storage que persiste em cookie no domínio raiz
// (araunahtech.com.br), permitindo SSO entre subdomínios (crm., composta., etc).
// Em dev/localhost cai em localStorage.
//
// Hardening 27/04 (review araunah-code-reviewer):
// - prefixo __Secure- em prod reforça que cookie só vai por HTTPS
// - migração legacy valida expires_at antes de promover (não ressuscita sessão expirada)
// - flag araunah-auth-migrated corta loop de migração após 1ª promoção
// - throw em overflow de chunks (>MAX_CHUNKS=20) pra não corromper sessão silenciosamente.
//   Budget atual: valor até MAX_CHUNK=1200 raw vai em cookie único (encodado no write);
//   acima disso o valor é encodado INTEIRO e fatiado em chunks JÁ-encodados de
//   MAX_CHUNK_ENC=3000 (20 × 3000 = 60KB encodado), gravados raw.
//
// Hardening 01/07 (auditoria) — formato de chunk v2:
// - setItem encoda a string inteira ANTES de fatiar (fatiar raw cortava surrogate
//   pair UTF-16 — emoji em nome vindo do Azure — no ponto de corte e o
//   encodeURIComponent por chunk estourava URIError → sessão não persistia)
// - chunk 0 carrega o total com prefixo "N|"; getItem valida a contagem e retorna
//   null se faltar chunk (antes, chunk intermediário evictado = JSON truncado
//   silencioso → requests anon)
// - RETROCOMPAT DE LEITURA: chunk 0 sem prefixo "N|" = formato antigo (chunks
//   decodáveis individualmente) e é lido como antes. Formato novo só na ESCRITA —
//   sessões existentes não são invalidadas pelo deploy.
// XSS surface ampliada pra *.araunahtech.com.br: depende de CSP estrita em todos os
// subdomínios listados em reference_dominios. Cookie precisa ser legível por JS
// (Supabase lê via storage adapter), portanto HttpOnly não é opção.

// 09/09/2026: o canonico virou araunah.com. Cookie de .araunahtech.com.br NAO e
// visivel em araunah.com (registrable domain diferente) — por isso o dominio do
// cookie sai do host atual, e nao de uma constante fixa.
const ROOT_DOMAINS = [".araunah.com", ".araunahtech.com.br"];

function cookieDomain(): string | null {
  if (typeof window === "undefined") return null;
  const h = window.location.hostname;
  return ROOT_DOMAINS.find((d) => h === d.slice(1) || h.endsWith(d)) ?? null;
}
const MAX_CHUNK = 1200; // 23/jun: era 3500; cookie URL-encodado + nome + attrs estourava ~4096B/cookie e o navegador descartava o chunk .0 -> sessao corrompida (login Microsoft nao persistia). 1200 raw cabe encodado.
// 01/07: fatias do formato v2 já estão encodadas (sem expansão posterior) — 3000
// chars + prefixo "N|" + nome + attrs ficam folgados abaixo do limite ~4096B/cookie.
const MAX_CHUNK_ENC = 3000;
const MAX_CHUNKS = 20;
const COOKIE_MAX_AGE = 60 * 60 * 24 * 30; // 30d
const SECURE_PREFIX = "__Secure-";
const MIGRATION_FLAG = "araunah-auth-migrated";

function isProdHost(): boolean {
  return cookieDomain() !== null;
}

// Em prod, cookies recebem prefixo __Secure-. Em dev (localhost), nome puro.
function cookieName(name: string): string {
  return isProdHost() ? `${SECURE_PREFIX}${name}` : name;
}

function cookieAttrs(maxAge: number): string {
  const parts = [`Path=/`, `SameSite=Lax`, `Max-Age=${maxAge}`];
  if (isProdHost()) {
    parts.unshift(`Domain=${cookieDomain()}`);
    parts.push("Secure");
  }
  return parts.join("; ");
}

function writeCookie(name: string, value: string) {
  document.cookie = `${cookieName(name)}=${encodeURIComponent(value)}; ${cookieAttrs(COOKIE_MAX_AGE)}`;
}

// Grava valor JÁ cookie-safe (fatia de encodeURIComponent + prefixo "N|") sem
// re-encodar — re-encodar triplicaria os '%' e estouraria o budget por cookie.
function writeCookieRaw(name: string, value: string) {
  document.cookie = `${cookieName(name)}=${value}; ${cookieAttrs(COOKIE_MAX_AGE)}`;
}

function dropCookie(name: string) {
  document.cookie = `${cookieName(name)}=; ${cookieAttrs(0)}`;
  // Também limpa nome sem prefixo (transição de versão antiga sem __Secure-)
  if (isProdHost()) {
    document.cookie = `${name}=; ${cookieAttrs(0)}`;
  }
}

// Lê o valor CRU do cookie (sem decodeURIComponent) — necessário pros chunks v2,
// cujas fatias podem terminar no meio de um escape %XX (decode por chunk quebraria).
function readCookieRaw(name: string): string | null {
  if (typeof document === "undefined") return null;
  const target = cookieName(name);
  const escaped = target.replace(/[.$?*|{}()[\]\\/+^]/g, "\\$&");
  const match = document.cookie.match(new RegExp("(?:^|; )" + escaped + "=([^;]*)"));
  if (match) return match[1];
  // Fallback transitório: lê cookie sem prefixo se existir (sessões pré-hardening)
  if (isProdHost()) {
    const escapedRaw = name.replace(/[.$?*|{}()[\]\\/+^]/g, "\\$&");
    const raw = document.cookie.match(new RegExp("(?:^|; )" + escapedRaw + "=([^;]*)"));
    if (raw) return raw[1];
  }
  return null;
}

function readCookie(name: string): string | null {
  const raw = readCookieRaw(name);
  return raw === null ? null : decodeURIComponent(raw);
}

// Promove valor legacy do localStorage somente se a sessão ainda for válida.
// Supabase serializa session como JSON com campo expires_at (epoch seconds).
function isLegacySessionFresh(serialized: string): boolean {
  try {
    const parsed = JSON.parse(serialized) as { expires_at?: number };
    if (typeof parsed.expires_at !== "number") return false;
    const nowSec = Math.floor(Date.now() / 1000);
    return parsed.expires_at > nowSec;
  } catch {
    return false;
  }
}

// Espelho em localStorage (por-ORIGEM, imune ao cap de ~180 cookies do domínio
// compartilhado araunahtech.com.br). O cookie do domínio raiz pode ser DESPEJADO
// quando o jar enche (ex.: entrar numa reunião Jitsi em meet.araunah.com,
// mesmo domínio pai) → getItem do cookie viraria null → logout. Com o espelho, a
// sessão sobrevive ao despejo do cookie. O cookie segue sendo escrito (SSO entre
// apps continua); o localStorage é só a rede de segurança de leitura.
function lsGet(k: string): string | null {
  try { return typeof localStorage !== "undefined" ? localStorage.getItem(k) : null; } catch { return null; }
}
function lsSet(k: string, v: string): void {
  try { if (typeof localStorage !== "undefined") localStorage.setItem(k, v); } catch { /* quota */ }
}
function lsDel(k: string): void {
  try { if (typeof localStorage !== "undefined") localStorage.removeItem(k); } catch { /* ignore */ }
}

export const crossDomainStorage = {
  getItem(key: string): string | null {
    if (typeof document === "undefined") return null;
    const head = readCookieRaw(`${key}.0`);
    if (head !== null) {
      // Formato v2 (01/07): chunk 0 = "N|<fatia>". N = total de chunks; as fatias
      // são o encodeURIComponent do valor INTEIRO fatiado — decodifica só após
      // reconcatenar. Detecção inequívoca: no formato antigo o chunk 0 cru começa
      // com o encode do JSON ("%7B..."), nunca com dígitos + "|" (o '|' literal
      // seria %7C sob encodeURIComponent).
      const v2 = /^(\d+)\|/.exec(head);
      if (v2) {
        const total = parseInt(v2[1], 10);
        let all = head.slice(v2[0].length);
        for (let i = 1; i < total; i++) {
          const chunk = readCookieRaw(`${key}.${i}`);
          // Chunk intermediário evictado → cookie incompleto. Cai pro espelho em
          // localStorage (sobrevive ao despejo); só retorna null se nem lá houver.
          if (chunk === null) return lsGet(key);
          all += chunk;
        }
        try {
          return decodeURIComponent(all);
        } catch {
          // Payload corrompido — pior seria devolver lixo pro supabase-js.
          return null;
        }
      }
      // Formato ANTIGO (pré-01/07): cada chunk foi encodado individualmente e não
      // há contagem — lê e decodifica chunk a chunk, como sempre foi.
      let all = "";
      try {
        all = decodeURIComponent(head);
      } catch {
        return null;
      }
      for (let i = 1; i < MAX_CHUNKS; i++) {
        const chunk = readCookieRaw(`${key}.${i}`);
        if (chunk === null) break;
        try {
          all += decodeURIComponent(chunk);
        } catch {
          return null;
        }
      }
      return all;
    }
    const single = readCookie(key);
    if (single !== null) return single;

    // Cookie do domínio compartilhado ausente (despejado quando o jar enche, ex.:
    // entrar em reunião Jitsi). Cai pro espelho em localStorage antes de desistir.
    const mirrored = lsGet(key);
    if (mirrored !== null) return mirrored;

    // Migração one-shot: promove sessão legada (localStorage crm-auth/composta-auth) pro cookie
    // SOMENTE se sessão ainda for válida (expires_at > now). Sessão expirada é dropada.
    // Flag araunah-auth-migrated corta o loop após 1ª passagem.
    if (typeof localStorage !== "undefined" && !localStorage.getItem(MIGRATION_FLAG)) {
      try { localStorage.setItem(MIGRATION_FLAG, "1"); } catch { /* quota */ }
      for (const legacyKey of ["crm-auth", "composta-auth", "portal-auth"]) {
        const legacy = localStorage.getItem(legacyKey);
        if (legacy && isLegacySessionFresh(legacy)) {
          this.setItem(key, legacy);
          try { localStorage.removeItem(legacyKey); } catch { /* ignore */ }
          return legacy;
        }
        // Sessão expirada → limpa o lixo do localStorage e segue
        if (legacy) {
          try { localStorage.removeItem(legacyKey); } catch { /* ignore */ }
        }
      }
    }
    return null;
  },
  setItem(key: string, value: string): void {
    if (typeof document === "undefined") return;
    // Sessao OAuth (Azure) traz provider_token/provider_refresh_token gigantes (token Microsoft)
    // e user_metadata inchado do Entra (custom_claims, iss, provider_id, preferred_username)
    // que o app nao usa e que estouram o budget de cookies multi-chunk -> sessao nao persiste
    // no reload. Enxugamos antes de gravar (mantendo nome/email/avatar no user_metadata)
    // pra caber como uma sessao normal (1-2 cookies), igual e-mail/senha.
    if (value && (value.indexOf("provider_token") !== -1 || value.indexOf("user_metadata") !== -1)) {
      try {
        const o = JSON.parse(value);
        let changed = false;
        if (o && (o.provider_token || o.provider_refresh_token)) {
          delete o.provider_token;
          delete o.provider_refresh_token;
          changed = true;
        }
        const meta = o?.user?.user_metadata as Record<string, unknown> | undefined;
        if (meta && typeof meta === "object") {
          for (const k of ["custom_claims", "iss", "provider_id", "preferred_username"]) {
            if (k in meta) {
              delete meta[k];
              changed = true;
            }
          }
        }
        if (changed) value = JSON.stringify(o);
      } catch { /* nao e JSON de sessao; ignora */ }
    }
    this.removeItem(key);
    // Espelho por-origem: sobrevive ao despejo do cookie do domínio compartilhado.
    lsSet(key, value);
    if (value.length <= MAX_CHUNK) {
      writeCookie(key, value);
      return;
    }
    // 01/07 (formato v2): encoda o valor INTEIRO e fatia o RESULTADO encodado.
    // Antes fatiava em code units UTF-16 e encodava por chunk — surrogate pair
    // (emoji em nome vindo do Azure) no ponto de corte quebrava o
    // encodeURIComponent com URIError e a sessão não persistia. As fatias já são
    // cookie-safe (saída de encodeURIComponent) e vão RAW; o total vai no chunk 0
    // com prefixo "N|" pro getItem validar contagem contra chunk evictado.
    const encoded = encodeURIComponent(value);
    const chunks = Math.ceil(encoded.length / MAX_CHUNK_ENC);
    if (chunks > MAX_CHUNKS) {
      // JWT estourou o budget de cookies. Setar parcialmente corromperia a sessão
      // silenciosamente. Aborta com throw pra estourar visível no Sentry / supabase-js retry.
      throw new Error(
        `crossDomainStorage: payload encodado ${encoded.length}B excede budget ${MAX_CHUNK_ENC * MAX_CHUNKS}B ` +
        `(${chunks} chunks > MAX_CHUNKS=${MAX_CHUNKS}). JWT custom claims muito grandes.`,
      );
    }
    for (let i = 0; i < chunks; i++) {
      const slice = encoded.slice(i * MAX_CHUNK_ENC, (i + 1) * MAX_CHUNK_ENC);
      writeCookieRaw(`${key}.${i}`, i === 0 ? `${chunks}|${slice}` : slice);
    }
  },
  removeItem(key: string): void {
    if (typeof document === "undefined") return;
    dropCookie(key);
    for (let i = 0; i < MAX_CHUNKS; i++) dropCookie(`${key}.${i}`);
    lsDel(key);
  },
};
